<template>
  <div style="overflow-x: hidden">
    <ul class="nav apim-nav-tabs">
      <li class="active">
        <router-link :to="{ name: 'collections-home' }">
          <i class="glyphicon glyphicon-home" /> Collections
        </router-link>
      </li>
      <li>
        <router-link :to="{ name: 'billing-home' }">
          <i class="glyphicon glyphicon-book" /> Subscriptions
          <span class="hidden-sm hidden-xs">&amp; Billing</span>
        </router-link>
      </li>
    </ul>
    <div class="tab-content active">
      <OverviewBoxes
        v-if="loaded && projects.length"
        :view="view"
        :data-array="allData"
        :earliest-date="earliestProjCreated"
      />
      <p class="info-label">
        Headline performance for all your collections, click on a metric to view in-depth data.
      </p>
      <div v-if="loaded">
        <p class="info-label text-right">
          Click on the <i class="glyphicon glyphicon-star-empty" /> to favorite the Collection to
          place it at the top of the list.
        </p>
        <Panel v-if="personalFaved" title="Personal Projects" class="panel-primary">
          <template #header="{ title }">
            <h3 class="panel-title">
              {{ title }}
              <span class="text-normal"> | </span>
              <span class="text-normal">{{ sortedDataNotInOrg.length + ' ' + view }}</span>
              <span class="text-normal"> | </span>
              <ProjectHeadlines
                style="display: inline"
                class="text-normal"
                :sorted-data="sortedDataNotInOrg"
              />
              <span class="pull-right">
                <a
                  v-tooltip="personalFaved ? 'Click to move to bottom' : 'Click to move to top'"
                  :aria-label="personalFaved ? 'Click to move to bottom' : 'Click to move to top'"
                  @click.stop="favOrg('public', !personalFaved)"
                  @keyup.enter="favOrg('public', !personalFaved)"
                >
                  <i v-if="personalFaved" class="glyphicon glyphicon-star" />
                  <i v-else class="glyphicon glyphicon-star-empty" />
                </a>
              </span>
            </h3>
          </template>
          <ProjectActions
            v-if="!projectsNotInOrg.length || projectsNotInOrg.length < 2"
            :projects="sortedDataNotInOrg"
            @open-api-expert="triggerApiExpertExpand(true)"
          />
          <ProjectsDataTable2
            :data="sortedDataNotInOrg"
            :loading="false"
            :show-col-picker="true"
            standalone
            @goto-project="goToProject($event)"
            @fav-project="favProject($event, true)"
            @unfav-project="favProject($event, false)"
            @sub-project="subProject($event, true)"
            @unsub-project="subProject($event, false)"
          />
          <ProjectCreate
            @project-created="
              $router.push({ name: 'project-home', query: { project_key_str: $event } })
            "
          />
        </Panel>

        <hr v-if="personalFaved && sortedOrgs.length" />

        <OrganizationPanel
          v-for="(org, idx) in sortedOrgs"
          :key="'org-panel-' + idx"
          v-model="filter"
          :organization="org"
          :sorted-data="sortedDataInOrg(org)"
          :initial-expand="idx < 2"
          :view="view"
          :idx="idx"
          :favorite="!!favOrgs.find((id) => org.id === id)"
          @sort-by="setSort($event)"
          @goto-project="goToProject($event)"
          @fav-project="favProject($event, true)"
          @unfav-project="favProject($event, false)"
          @fav-org="favOrg($event, true)"
          @unfav-org="favOrg($event, false)"
          @sub-project="subProject($event, true)"
          @unsub-project="subProject($event, false)"
        />

        <Panel v-if="!personalFaved" title="Personal Projects" class="panel-primary">
          <template #header="{ title }">
            <h3 class="panel-title">
              {{ title }}
              <span class="text-normal"> | </span>
              <span class="text-normal">{{ sortedDataNotInOrg.length + ' ' + view }}</span>
              <span class="text-normal"> | </span>
              <ProjectHeadlines
                style="display: inline"
                class="text-normal"
                :sorted-data="sortedDataNotInOrg"
              />
              <span class="pull-right">
                <a
                  v-tooltip="personalFaved ? 'Click to move to bottom' : 'Click to move to top'"
                  :aria-label="personalFaved ? 'Click to move to bottom' : 'Click to move to top'"
                  @click.stop="favOrg('public', !personalFaved)"
                  @keyup.enter="favOrg('public', !personalFaved)"
                >
                  <i v-if="personalFaved" class="glyphicon glyphicon-star" />
                  <i v-else class="glyphicon glyphicon-star-empty" />
                </a>
              </span>
            </h3>
          </template>
          <ProjectsDataTable2
            v-if="sortedDataNotInOrg.length"
            :data="sortedDataNotInOrg"
            :loading="false"
            :show-col-picker="true"
            standalone
            @goto-project="goToProject($event)"
            @fav-project="favProject($event, true)"
            @unfav-project="favProject($event, false)"
            @sub-project="subProject($event, true)"
            @unsub-project="subProject($event, false)"
          />
          <ProjectCreate
            @project-created="
              $router.push({ name: 'project-home', query: { project_key_str: $event } })
            "
          />
          <ProjectActions
            v-if="!projectsNotInOrg.length || projectsNotInOrg.length < 2"
            :projects="sortedDataNotInOrg"
            @open-api-expert="triggerApiExpertExpand(true)"
          />
        </Panel>

        <hr v-if="sortedOrgs.length && !personalFaved" />
        <ApiExpertPanel
          ref="apiExpertPanel"
          :initial-expand="false"
          :trigger-expand="apiExpertExpand"
          @expanded="apiExpertExpand = $event"
        />
      </div>
      <LoadingAnim v-else />
    </div>
  </div>
</template>

<script>
import _chunk from 'lodash/chunk';
import _concat from 'lodash/concat';
import _flatten from 'lodash/flatten';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import moment from 'moment-timezone';
import { get, call } from 'vuex-pathify';
import Panel from '../components/Panel.vue';
import OrganizationPanel from '../components/projects/OrganizationPanel.vue';
import ApiExpertPanel from '../components/projects/ApiExpertPanel.vue';
import ProjectsDataTable2 from '../components/projects/ProjectsDataTable2.vue';
import ProjectHeadlines from '../components/projects/ProjectHeadlines.vue';
import ProjectActions from '../components/projects/ProjectActions.vue';
import OverviewBoxes from '../components/organization/OverviewBoxes.vue';
import ProjectCreate from '../components/organization/ProjectCreate.vue';
import sortDataBy from '../js/sorting';

const AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;
const CHUNK_SIZE = 3;

export default {
  name: 'CollectionsHome',
  components: {
    Panel,
    OverviewBoxes,
    OrganizationPanel,
    ProjectsDataTable2,
    ProjectHeadlines,
    ProjectActions,
    ProjectCreate,
    ApiExpertPanel,
  },
  data() {
    return {
      projectInfo: {},
      sortBy: 'favorite',
      filter: '',
      favProjects: [],
      favOrgs: [],
      projectSubs: {},
      apiExpertExpand: false,
    };
  },
  computed: {
    view() {
      const hash = this.$route.hash || '#projects';
      return hash.slice(1);
    },
    sortedOrgs() {
      // const orgIds = Object.keys(this.roles || {});
      // const orgs = orgIds.map((orgId) => this.organizations[orgId]);
      const orgs = Object.values(this.organizations)
        .filter((org) => this.projectsInOrg(org).length)
        .map((org) => ({ ...org, favorite: !!this.favOrgs.find((o) => o === org.id) }));
      return sortDataBy(orgs, 'favorite', false);
    },
    projectsNotInOrg() {
      return this.projects.filter((proj) => !proj.project.org_id);
    },
    sortedDataNotInOrg() {
      const fullData = this.buildData(this.projectsNotInOrg);
      return this.sortData(fullData);
    },
    earliestProjCreated() {
      return this.projects.map(({ created }) => created).sort()[0] || '';
    },
    allData() {
      if (this.loaded) {
        return this.buildData(this.projects);
      }
      return [];
    },
    projects() {
      return Object.values(this.projectsMap);
    },
    personalFaved() {
      return this.favOrgs.find((o) => o === 'public');
    },
    loaded: get('account2/loaded'),
    projectId: get('account2/projectId'),
    projectsMap: get('account2/projects'),
    organizations: get('account2/organizations'),
    roles: get('account2/roles'),
    projectName: get('account2/projects[:projectId].project.name'),
  },
  watch: {
    projects() {
      this.fetchData();
    },
    projectsNotInOrg(val) {
      // If we only have one project, go to project home for now
      // Only do this on load, so we can go back to this page is needed
      if (val.length === 1 && this.projects.length === 1) {
        this.goToProject(val[0].id);
      }
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.apiExpertExpand = false;

      const currentPage = this.$route.path; // For checking the page hasn't changed

      const claims = await this.$auth.getIdTokenClaims();
      if (claims) {
        this.favProjects = claims[`${AUDIENCE}/fav_projects`];
        this.favOrgs = claims[`${AUDIENCE}/fav_orgs`];
      }

      this.requireAgentInfo();

      const allProjects = _concat(
        _flatten(this.sortedOrgs.map((org) => this.projectsInOrg(org))),
        this.projectsNotInOrg,
      );

      const orgDataFutures = this.sortedOrgs.map((org) => this.getOrgData(org));
      orgDataFutures.push(this.getOrgData({ id: 'public' }));

      await orgDataFutures.forEach(async (future) => {
        const { synopses } = await future;
        synopses.forEach((synopsis) => {
          const projectInfo = this.projectInfo[synopsis.project_id] || {};
          this.$set(this.projectInfo, synopsis.project_id, {
            ...projectInfo,
            synopsis,
          });
        });
      });

      const projChunks = _chunk(allProjects, CHUNK_SIZE);

      // Running through chunks calls sequentially, so we don't
      // load all data if the page changes
      for (let i = 0; i < projChunks.length; i += 1) {
        if (currentPage !== this.$route.path) {
          return;
        }
        const projects = projChunks[i];

        // Load data in parallel within the chunk
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(projects.map(this.buildDataLoader(currentPage)));
      }

      if (currentPage !== this.$route.path) {
        return;
      }

      try {
        const { results } = await this.$fetcher.getProjectSubscriptions();
        this.projectSubs = results.reduce(
          (soFar, { project_id: projectId, options }) => ({ ...soFar, [projectId]: options }),
          {},
        );
      } catch (ex) {
        console.log(ex);
      }
    },
    lastWeek() {
      return moment.utc().subtract(2, 'hours').startOf('day').subtract(1, 'week').format();
    },
    async getOrgData({ id }) {
      return this.$fetcher
        .getOrgSynopsis({ orgId: id, kind: 'WEEK', time: this.lastWeek() })
        .catch((ex) => {
          console.log(ex);
          return { synopses: [] };
        });
    },
    buildDataLoader(currentPage) {
      // Returns an async arrow function that can be passed into .map
      return async (proj) => {
        const data = await this.loadDataForProject(proj, currentPage);
        // $set is potentially expensive, so check for page change
        if (currentPage !== this.$route.path) {
          return;
        }
        const existing = this.projectInfo[proj.id] || {};
        this.$set(this.projectInfo, proj.id, Object.freeze({ ...existing, ...data }));
      };
    },
    buildData(projects) {
      const dataArrays = projects.map((proj) => {
        const results = this.getResults(proj);
        const subscribed = !!this.getProjSubs(proj);
        const synopsis = this.getSynopsis(proj);

        return {
          projectId: proj.id,
          favorite: !!this.favProjects.find((id) => id === proj.id.toLowerCase()),
          name: proj.project.name,
          accessLevel: proj.access_level,
          systemTags: proj.project.system_tags,
          subscribed,
          results,
          synopsis,
        };
      });

      return _flatten(dataArrays).filter(
        ({ name, subName = null }) =>
          !this.filter ||
          name.toLowerCase().includes(this.filter.toLowerCase()) ||
          (subName && subName.toLowerCase().includes(this.filter.toLowerCase())),
      );
    },

    projectsInOrg({ id }) {
      const projects = this.projects.filter((proj) => proj.project.org_id === id);
      return projects;
    },
    sortedDataInOrg({ id }) {
      const projects = this.projectsInOrg({ id });
      const data = this.buildData(projects);
      return this.sortData(data);
    },
    sortData(projects) {
      const reversed = this.sortBy[0] === '-';
      const sortAttr = reversed ? this.sortBy.slice(1) : this.sortBy;
      return sortDataBy(projects, sortAttr, reversed);
    },
    async loadDataForProject({ id: projectId }, currentPage) {
      const limit = 100;
      const projData = {};

      try {
        const { meta, results } = await this.$fetcher.getResults({ projectId, limit });
        projData.results = results;
        projData.meta = meta;

        // Have we navigated away? Just return
        if (currentPage !== this.$route.path) {
          return projData;
        }

        if (!results.find((res) => res.meta.result_category === 'FAIL')) {
          // console.log('No failure in most recent results');
          try {
            const { results: failResults } = await this.$fetcher.getResults({
              projectId,
              limit: 1,
              resultCategory: 'FAIL',
            });
            projData.results = [...results, ...failResults];
          } catch (ex) {
            console.warn(ex);
          }
        }
      } catch (ex) {
        console.warn(ex);
        projData.meta = {};
        projData.results = [];
      }

      return projData;
    },
    getMeta({ id }) {
      return _get(this.projectInfo, [id, 'meta'], {});
    },
    getResults({ id }) {
      return _get(this.projectInfo, [id, 'results'], []);
    },
    getProjSubs({ id }) {
      return _get(this.projectSubs, id, false);
    },
    getSynopsis({ id }) {
      return _get(this.projectInfo, [id, 'synopsis'], null);
    },
    setSort(sortByCol) {
      this.sortBy = sortByCol === this.sortBy ? `-${sortByCol}` : sortByCol;
    },
    favProject(projectId, add) {
      const lowerProjId = projectId.toLowerCase();
      if (add) {
        this.favProjects = _uniq([...this.favProjects.map((p) => p.toLowerCase()), lowerProjId]);
      } else {
        this.favProjects = [...this.favProjects.filter((id) => id !== lowerProjId)];
      }
      return this.$fetcher.updateAccount({ data: { fav_projects: this.favProjects } });
    },
    favOrg(orgId, add) {
      const lowerOrgId = orgId.toLowerCase();
      if (add) {
        this.favOrgs = _uniq([...this.favOrgs.map((p) => p.toLowerCase()), lowerOrgId]);
      } else {
        this.favOrgs = [...this.favOrgs.filter((id) => id !== lowerOrgId)];
      }
      return this.$fetcher.updateAccount({ data: { fav_orgs: this.favOrgs } });
    },
    async subProject(projectId, add) {
      if (add) {
        const out = await this.$fetcher.setProjectSubscriptions(true, true, true, projectId);
        this.$set(this.projectSubs, projectId, ['daily', 'weekly', 'monthly']);
        return out;
      }
      const out = await this.$fetcher.removeProjectSubscriptions(projectId);
      this.$set(this.projectSubs, projectId, undefined);
      return out;
    },
    triggerApiExpertExpand(val) {
      this.apiExpertExpand = val;
      const element = this.$refs.apiExpertPanel;
      const top = element.$el.offsetTop;
      window.scrollTo({ left: 0, top, behavior: 'smooth' });
    },
    async goToProject(id) {
      await this.setProject({ id });
      this.$router.push({ name: 'project-home', query: { project_key_str: id } });
    },
    requireAgentInfo: call('apimAPI/requireAgentInfo'),
    setProject: call('account2/setProject'),
  },
};
</script>

<style scoped>
.text-normal {
  color: var(--light-main);
}
</style>
